import { Operator } from '../types/database';
import { getRolePermissions } from '../services/rolePermissions';

/**
 * Permission types available in the system
 */
export enum Permission {
  // PC Control permissions
  LAUNCH_EXPERIENCE = 'launch_experience',
  // Launch while the Logitech headphone is low/disconnected (PICO must still be connected)
  LAUNCH_WITHOUT_CHARGED_HEADSET = 'launch_without_charged_headset',
  KILL_EXPERIENCE = 'kill_experience',
  INVALIDATE_EXPERIENCE_RUN = 'invalidate_experience_run',
  SHUTDOWN_PC = 'shutdown_pc',
  RESTART_PC = 'restart_pc',
  WAKE_ON_LAN = 'wake_on_lan',

  // Volume control
  ADJUST_VOLUME = 'adjust_volume',

  // Configuration permissions
  VIEW_PC_DETAILS = 'view_pc_details',
  VIEW_NETWORK_INFO = 'view_network_info',

  // Day closing
  DAG_AFSLUITING = 'dag_afsluiting',

  // Agent control
  RESTART_AGENT = 'restart_agent',

  // Start/restart Parsec (remote-desktop host) on a PC
  CONTROL_PARSEC = 'control_parsec',

  // Checklist
  REQUIRE_CHECKLIST = 'require_checklist',

  // Release details (version string, beta/alpha badges)
  VIEW_RELEASE_DETAILS = 'view_release_details',

  // Access to beta/alpha builds
  ACCESS_BETA_RELEASES = 'access_beta_releases',

  // Next level & scene select during running experiences
  CONTROL_EXPERIENCE_LEVELS = 'control_experience_levels',

  // Mark an operational issue (writes an operations.incidents marker)
  MARK_ISSUE = 'mark_issue',

  // Play a preset voice warning/instruction to the visitor in the headset
  SEND_VISITOR_MESSAGE = 'send_visitor_message',
}

/**
 * Check if an operator has a specific permission based on their role
 * This checks against the database-defined role permissions
 */
export async function hasPermission(operator: Operator | null, permission: Permission | string): Promise<boolean> {
  if (!operator) return false;

  // Get permissions for the operator's role from the database
  const rolePermissions = await getRolePermissions(operator.role);

  return rolePermissions.includes(permission as string);
}

/**
 * Synchronous version - checks if permission is in cached operator permissions
 * Use this when you already have the permissions loaded
 */
export function hasPermissionSync(operator: Operator | null, permission: Permission | string): boolean {
  if (!operator) return false;

  // Check if permission exists in operator's cached permissions
  return operator.permissions?.[permission] === true;
}

/**
 * Check if an operator has admin privileges
 */
export function isAdmin(operator: Operator | null): boolean {
  if (!operator) return false;
  return operator.role === 'admin';
}

/**
 * Check if an operator can control PCs (launch, kill, shutdown, restart)
 */
export async function canControlPCs(operator: Operator | null): Promise<boolean> {
  if (!operator) return false;

  const permissions = await getRolePermissions(operator.role);

  return (
    permissions.includes(Permission.LAUNCH_EXPERIENCE) ||
    permissions.includes(Permission.KILL_EXPERIENCE) ||
    permissions.includes(Permission.SHUTDOWN_PC) ||
    permissions.includes(Permission.RESTART_PC)
  );
}

/**
 * Get a list of all permissions an operator has based on their role
 */
export async function getOperatorPermissions(operator: Operator | null): Promise<string[]> {
  if (!operator) return [];

  return await getRolePermissions(operator.role);
}
